import { createFileRoute } from '@tanstack/react-router';
import { Box, Typography, Button } from '@mui/material';
import { CreateTokenBySsoDocument } from '@amber-ui/core';

export const Route = createFileRoute('/sso/callback')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
    stytch_token_type: (search.stytch_token_type as string) || '',
  }),
  loaderDeps: ({ search }) => ({
    token: search.token,
    stytch_token_type: search.stytch_token_type,
  }),
  shouldReload: false,
  loader: async ({ context: { apolloClient }, deps }) => {
    // SSO callback token exchange — runs exactly once per navigation.
    // Non-idempotent (Stytch token is single-use), so it MUST NOT live in a
    // useEffect where React StrictMode or dep instability can double-fire it.
    const { token, stytch_token_type: tokenType } = deps;

    if (tokenType !== 'sso' || !token) {
      return { error: 'Invalid SSO callback. Please use "Sign in with SSO" from the login page.' };
    }

    try {
      const result = await apolloClient.mutate({
        mutation: CreateTokenBySsoDocument,
        variables: { sso_token: token },
      });

      const authToken = result.data?.createTokenBySSO?.token;
      if (!authToken) {
        return { error: 'Unexpected server response. Please try again.' };
      }

      // Follows the same pattern as the standard login flow in LoginForm.tsx:
      // localStorage.setItem('token') + redirect to root triggers
      // initializeAuth() on page load, which calls renewToken and populates
      // the full auth context (account_id, user_id, etc.)
      // IMPORTANT: Do NOT use window.location.reload() here — it preserves the
      // URL including the ?token= param, causing the loader to fire again on the
      // reloaded page with the already-consumed single-use Stytch token.
      localStorage.setItem('token', authToken);
      window.location.replace('/');

      // Return placeholder — reload exits before the component renders
      return { error: null };
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'SSO authentication failed. Please try again.';
      return { error: message };
    }
  },
  component: SSOCallbackPage,
});

function SSOCallbackPage() {
  const { error } = Route.useLoaderData();

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          SSO Login Failed
        </Typography>
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="outlined" href="/login">
          Return to Login
        </Button>
      </Box>
    );
  }

  // This renders briefly before window.location.reload() takes effect
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <Typography>Completing SSO login...</Typography>
    </Box>
  );
}
